import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap';
import CountDown from './Countdown';
import "./ConfirmReservationModal.css";
import './Checkout.css';
import axios from 'axios';
import { User } from 'firebase/auth';
import { SLOT_UNAVAILABLE_MESSAGE } from '../utils/teeSlotCapacity';

declare global {
    interface Window {
        Square: any;
        ApplePaySession?: {
            canMakePayments(): boolean;
        };
        google?: {
            payments: any;
        };
    }
    const ApplePaySession: {
        canMakePayments(): boolean;
    };
}

interface Customer {
    email: string | null;
    phone: string | null;
    firstName: string | null;
    lastName: string | null;
    paid: boolean;
}

interface PayNowModalProps {
    customer: Customer;
    checkoutError: string | null;
    setCheckoutError: (error: string | null) => void;
    cartTotal: number;
    widgetSettings: any;
    updateReservationReceipt: (receipt: any, provider: string) => void;
    showPaymentModal: boolean;
    setShowPaymentModal: (show: boolean) => void;
    requirePayment: boolean;
    reservation: any;
    setReservation: (reservation: any) => void;
    setPageLoaderStatus: (status: boolean, ariaLabel?: string) => void;
    finalizeReservation: () => boolean | Promise<boolean>;
    onPaymentSucceeded?: () => void;
    assertSlotAvailable: () => Promise<boolean>;
    removeReservationTime: () => void;
    authData: {
        apiLoginID: string;
        clientKey: string;
    };
    userData: any;
    user: User | null;
    course: any;
}

const Square = window.Square;

// Function to dynamically load the correct Square SDK based on environment
const loadSquareSDK = async (environment: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        // Check if Square SDK is already loaded
        if (window.Square) {
            console.log('Square SDK already loaded, checking if it matches environment:', environment);

            // Check if the loaded SDK matches our environment
            const currentScript = document.querySelector('script[src*="squarecdn.com"]');
            if (currentScript) {
                const src = (currentScript as HTMLScriptElement).src;
                console.log('Current Square SDK source:', src);

                const isSandbox = src.includes('sandbox');
                const needsSandbox = environment === 'sandbox';

                if (isSandbox === needsSandbox) {
                    console.log('Square SDK environment matches, using existing SDK');
                    resolve(window.Square);
                    return;
                } else {
                    console.log('Square SDK environment mismatch, removing existing SDK');
                    // Remove existing SDK
                    currentScript.remove();
                    delete window.Square;
                }
            }
        }

        // Determine which SDK to load based on environment
        const sdkUrl = environment === 'sandbox'
            ? 'https://sandbox.web.squarecdn.com/v1/square.js'
            : 'https://web.squarecdn.com/v1/square.js';

        console.log(`Loading Square SDK from: ${sdkUrl} for environment: ${environment}`);

        // Create script element
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.async = true;
        script.onload = () => {
            console.log('Square SDK loaded successfully from:', sdkUrl);
            resolve(window.Square);
        };
        script.onerror = () => {
            console.error('Failed to load Square SDK from:', sdkUrl);
            reject(new Error('Failed to load Square SDK'));
        };

        // Add script to document
        document.head.appendChild(script);
    });
};

/** Only used while the payment modal is open — avoids noisy errors on other screens (e.g. course picker). */
function computeCartTotalForPayment(reservation: any, course: any): number {
    let total = 0;

    console.log('=== Starting Cart Total Calculation ===');
    console.log('Reservation:', {
        id: reservation?.id,
        numHoles: reservation?.numHoles,
        numPlayers: reservation?.numPlayers,
        golfers: reservation?.golfers
    });

    if (!reservation?.golfers || !Array.isArray(reservation.golfers)) {
        console.error('No golfers found in reservation');
        return 0;
    }

    if (!course?.rates) {
        console.error('No course rates found');
        return 0;
    }

    console.log('Course rates:', course.rates);
    console.log('Processing golfers:', reservation.golfers.length);

    reservation.golfers.forEach((golfer, index) => {
        console.log(`\nProcessing Golfer ${index + 1}:`, {
            name: golfer.name,
            rateGroup: golfer.rateGroup,
            fullGolfer: golfer
        });

        const rateGroup = typeof golfer.rateGroup === 'number' ? golfer.rateGroup : 0;
        console.log(`Using rate group for golfer ${index + 1}:`, rateGroup);

        let fee = 0;

        const rate = course.rates[rateGroup];
        console.log(`Rate for golfer ${index + 1}:`, {
            rateGroup,
            rate
        });

        if (!rate) {
            console.error(`No rate found for rate group ${rateGroup}, using default rate`);
            const defaultRate = course.rates[0];
            if (defaultRate) {
                if (reservation.numHoles === 18) {
                    fee = Number(defaultRate.greenFee18) || 0;
                } else if (reservation.numHoles === 9) {
                    fee = Number(defaultRate.greenFee9) || 0;
                }
            }
        } else {
            if (reservation.numHoles === 18) {
                fee = Number(rate.greenFee18) || 0;
                console.log(`18-hole fee for golfer ${index + 1}:`, {
                    rate: rate.greenFee18,
                    default: 0,
                    selected: fee
                });
            } else if (reservation.numHoles === 9) {
                fee = Number(rate.greenFee9) || 0;
                console.log(`9-hole fee for golfer ${index + 1}:`, {
                    rate: rate.greenFee9,
                    default: 0,
                    selected: fee
                });
            }
        }

        fee = Number(fee) || 0;
        total = Number(total) + fee;
        console.log(`Running total after golfer ${index + 1}: $${total}`);
    });

    total = Number(total) || 0;

    console.log('\n=== Final Cart Total Calculation ===');
    console.log('Total amount:', {
        raw: total,
        formatted: `$${total.toFixed(2)}`,
        cents: Math.round(total * 100)
    });
    console.log('================================\n');

    return total;
}

function PayNowModal({
    widgetSettings,
    updateReservationReceipt,
    requirePayment,
    showPaymentModal,
    setShowPaymentModal,
    reservation,
    setReservation,
    finalizeReservation,
    onPaymentSucceeded,
    assertSlotAvailable,
    removeReservationTime,
    userData,
    user,
    setPageLoaderStatus,
    setCheckoutError,
    checkoutError,
    course
}: Omit<PayNowModalProps, 'cartTotal'>) {
    const [completePaymentLoader, setCompletePaymentLoader] = useState(false);
    const [cardToken, setCardToken] = useState<string | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<{
        card: any;
        applePay: any;
        googlePay: any;
    }>({
        card: null,
        applePay: null,
        googlePay: null
    });
    const [availablePaymentMethods, setAvailablePaymentMethods] = useState<string[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('Payment successful!');
    const [isCardFieldsLoaded, setIsCardFieldsLoaded] = useState(false);
    const [card, setCard] = useState<any>(null);
    const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(false);

    const cartTotal = useMemo(() => {
        if (!showPaymentModal) return 0;
        return computeCartTotalForPayment(reservation, course);
    }, [showPaymentModal, reservation, course]);

    const handleCancel = () => {
        removeReservationTime();
        setShowPaymentModal(false);
        setCompletePaymentLoader(false);
    }

    // Initialize Square Web Payments SDK
    const initializeSquare = async () => {
        try {
            // Determine which application ID to use based on environment
            const environment = widgetSettings?.paymentGateway?.environment;
            let squareApplicationId;
            let squareLocationId;
            if (environment === 'sandbox') {
                squareApplicationId = widgetSettings?.paymentGateway?.squareSandboxApplicationId;
                squareLocationId = widgetSettings?.paymentGateway?.squareSandboxLocationId;
                console.log('Using sandbox application ID:', squareApplicationId);
                console.log('Using sandbox location ID:', squareLocationId);
            } else {
                squareApplicationId = widgetSettings?.paymentGateway?.squareApplicationId;
                squareLocationId = widgetSettings?.paymentGateway?.squareLocationId;
                console.log('Using production application ID:', squareApplicationId);
                console.log('Using production location ID:', squareLocationId);
            }

            console.log('Credential check:', {
                squareApplicationId: !!squareApplicationId,
                squareLocationId: !!squareLocationId,
                environment: environment || 'production'
            });

            if (!squareApplicationId || !squareLocationId) {
                console.error('Missing credentials:', {
                    hasApplicationId: !!squareApplicationId,
                    hasLocationId: !!squareLocationId,
                    applicationId: squareApplicationId,
                    locationId: squareLocationId
                });
                throw new Error('Square configuration missing. Please check your Square settings in the course configuration.');
            }

            // Load the correct Square SDK based on environment
            const SquareSDK = await loadSquareSDK(environment || 'production');

            console.log('Initializing Square payments with:', {
                applicationId: squareApplicationId,
                locationId: squareLocationId,
                environment: environment || 'production'
            });

            const payments = await SquareSDK.payments(squareApplicationId, squareLocationId);
            console.log('Square payments initialized:', payments);

            // Initialize card payment first
            const cardContainer = document.getElementById('card-container');
            console.log('Card container element:', cardContainer);

            if (!cardContainer) {
                throw new Error('Card container not found');
            }

            const card = await payments.card({
                style: {
                    input: {
                        fontSize: '16px',
                        color: '#333333'
                    },
                    'input::placeholder': {
                        color: '#999999'
                    }
                }
            });
            console.log('Card payment method initialized:', card);

            await card.attach('#card-container');
            console.log('Card payment method attached to container');

            // Add event listeners for card events
            const errorListener = (event: any) => {
                console.log('Card error changed:', event.detail);
            };
            const brandListener = (event: any) => {
                console.log('Card brand changed:', event.detail);
            };
            const tokenizeListener = (event: any) => {
                console.log('Card tokenized:', event.detail);
                if (event.detail.token) {
                    console.log('Setting card token:', event.detail.token);
                    setCardToken(event.detail.token);
                } else {
                    console.error('Tokenization failed:', event.detail);
                }
            };
            const validationListener = (event: any) => {
                console.log('Card validation state:', event.detail);
            };

            card.addEventListener('errorChanged', errorListener);
            card.addEventListener('cardBrandChanged', brandListener);
            card.addEventListener('tokenize', tokenizeListener);
            card.addEventListener('validationStateChanged', validationListener);

            setPaymentMethods(prev => ({
                ...prev,
                card
            }));

            // Only proceed with Apple Pay and Google Pay if card initialization was successful
            if (card) {
                // Initialize Apple Pay
                let applePay = null;
                console.log('Checking Apple Pay availability:', {
                    hasApplePaySession: !!window.ApplePaySession,
                    canMakePayments: window.ApplePaySession?.canMakePayments(),
                    merchantId: widgetSettings?.paymentGateway?.applePayMerchantId,
                    widgetSettings: widgetSettings?.paymentGateway
                });

                if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
                    try {
                        console.log('Initializing Apple Pay with settings:', {
                            amount: cartTotal,
                            currency: 'USD',
                            countryCode: 'US',
                            label: 'TeeRocket',
                            merchantCapabilities: ['supports3DS'],
                            merchantId: widgetSettings?.paymentGateway?.applePayMerchantId
                        });

                        applePay = await payments.applePay({
                            amount: cartTotal,
                            currency: 'USD',
                            countryCode: 'US',
                            label: 'TeeRocket',
                            merchantCapabilities: ['supports3DS'],
                            merchantId: widgetSettings?.paymentGateway?.applePayMerchantId,
                        });

                        console.log('Apple Pay initialized successfully');
                        await applePay.attach('#apple-pay-button');
                        console.log('Apple Pay button attached to container');
                    } catch (error) {
                        console.error('Apple Pay initialization failed:', error);
                    }
                } else {
                    console.log('Apple Pay not available:', {
                        hasApplePaySession: !!window.ApplePaySession,
                        canMakePayments: window.ApplePaySession?.canMakePayments()
                    });
                }

                // Initialize Google Pay
                let googlePay = null;
                if (window.google && window.google.payments) {
                    try {
                        googlePay = await payments.googlePay({
                            amount: cartTotal,
                            currency: 'USD',
                            countryCode: 'US',
                            environment: 'TEST',
                        });
                        await googlePay.attach('#google-pay-button');
                        console.log('Google Pay initialized and attached');
                    } catch (error) {
                        console.error('Google Pay initialization failed:', error);
                    }
                }

                setPaymentMethods(prev => ({
                    ...prev,
                    applePay,
                    googlePay
                }));

                const available = ['card'];
                if (applePay) available.push('applePay');
                if (googlePay) available.push('googlePay');
                setAvailablePaymentMethods(available);
            }
        } catch (error) {
            console.error('Error initializing Square payments:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                widgetSettings: widgetSettings,
                paymentGateway: widgetSettings?.paymentGateway
            });

            let errorMessage = 'Error initializing payment system. Please try again later.';

            if (error.message.includes('Square SDK not loaded')) {
                errorMessage = 'Square payment system failed to load. Please refresh the page and try again.';
            } else if (error.message.includes('Square configuration missing')) {
                errorMessage = 'Payment configuration is missing. Please contact support.';
            } else if (error.message.includes('Card container not found')) {
                errorMessage = 'Payment form failed to load. Please refresh the page.';
            }

            setCheckoutError(errorMessage);
        }
    };

    const handlePayment = async (paymentMethod: 'card' | 'applePay' | 'googlePay') => {
        console.log('\n=== Processing Payment ===');
        console.log('Payment method:', paymentMethod);
        console.log('Cart total:', {
            raw: cartTotal,
            formatted: `$${cartTotal.toFixed(2)}`,
            cents: Math.round(cartTotal * 100)
        });

        setCompletePaymentLoader(true);
        try {
            let sourceId;

            if (paymentMethod === 'card') {
                console.log('Current card token state:', cardToken);
                if (!cardToken) {
                    console.log('No card token available, attempting to tokenize...');
                    try {
                        const result = await paymentMethods.card.tokenize();
                        console.log('Tokenization result:', result);
                        if (result.status === 'OK') {
                            sourceId = result.token;
                        } else {
                            throw new Error(result.errors[0].message);
                        }
                    } catch (error) {
                        console.error('Tokenization error:', error);
                        setCheckoutError("Please complete the payment form");
                        setCompletePaymentLoader(false);
                        return;
                    }
                } else {
                    sourceId = cardToken;
                }
            } else {
                // For Apple Pay and Google Pay, we'll get the token directly
                const payment = paymentMethods[paymentMethod];
                const result = await payment.tokenize();
                if (result.status === 'OK') {
                    sourceId = result.token;
                } else {
                    throw new Error(result.errors[0].message);
                }
            }

            const groupId = localStorage.getItem('group');
            const courseId = localStorage.getItem('course');

            if (!groupId || !courseId) {
                setCheckoutError('Missing required course information');
                setCompletePaymentLoader(false);
                return;
            }

            if (!user) {
                setCheckoutError('You must be signed in to complete payment.');
                setCompletePaymentLoader(false);
                return;
            }

            const idToken = await user.getIdToken();

            const { data } = await axios.post(
                'https://us-central1-teerocket.cloudfunctions.net/squarePaymentProcessorV2',
                {
                    sourceId,
                    amount: cartTotal * 100, // Convert to cents
                    reservationId: reservation.id,
                    groupId,
                    courseId,
                    reservationDetails: {
                        courseName: course?.name,
                        numHoles: reservation.numHoles,
                        numPlayers: reservation.numPlayers,
                        golfers: reservation.golfers.map(golfer => {
                            const rateGroup = typeof golfer.rateGroup === 'number' ? golfer.rateGroup : 0;
                            const rate = course?.rates?.[rateGroup];
                            return {
                                name: golfer.name,
                                rateName: rate?.name || 'Standard Rate'
                            };
                        })
                    }
                },
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                }
            );

            console.log('Payment response:', data);

            if (data.success && data.confirmed) {
                const shouldMarkAsPaid = data.paymentBehavior !== 'authorize';
                const updatedReservation = {
                    ...reservation,
                    status: 'confirmed',
                    expiration: null,
                    expiresAt: null,
                    golfers: reservation.golfers.map((golfer: { paid?: boolean }) => ({
                        ...golfer,
                        paid: shouldMarkAsPaid,
                    })),
                    paymentInfo: data.payment
                        ? {
                              paymentId: data.payment?.payment?.id || data.payment?.id,
                              paymentBehavior: data.paymentBehavior,
                              status: data.status,
                              amount: cartTotal,
                              timestamp: new Date().toISOString(),
                          }
                        : reservation.paymentInfo,
                };

                if (data.payment) {
                    await updateReservationReceipt(data.payment, 'square');
                }
                setReservation(updatedReservation);

                if (data.paymentBehavior === 'authorize') {
                    setSuccessMessage('Reservation confirmed. Payment hold authorized on your card.');
                } else {
                    setSuccessMessage('Reservation confirmed and payment completed.');
                }

                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                    setShowPaymentModal(false);
                    if (onPaymentSucceeded) {
                        onPaymentSucceeded();
                    }
                }, 1200);
                return;
            }

            if (data.success && !data.confirmed) {
                setCheckoutError(
                    'Payment was received but the reservation could not be confirmed. Please contact the course.'
                );
                return;
            }

            setCheckoutError(data.error || 'Payment failed. Please try again.');
        } catch (err: any) {
            const apiError = err.response?.data;
            if (apiError?.code === 'SLOT_UNAVAILABLE') {
                setCheckoutError(SLOT_UNAVAILABLE_MESSAGE);
            } else if (apiError?.paymentVoided) {
                setCheckoutError(
                    apiError.error ||
                        'That tee time is no longer available. Your card was not charged.'
                );
            } else {
                setCheckoutError(
                    apiError?.error || err.message || 'Payment failed. Please try again.'
                );
            }
        } finally {
            setCompletePaymentLoader(false);
        }
    }

    // Effect to handle initial loading state when modal opens
    useEffect(() => {
        if (showPaymentModal) {
            setIsPaymentMethodsLoading(true);
            // Set a small delay to show the loading state
            const timer = setTimeout(() => {
                setIsPaymentMethodsLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [showPaymentModal]);

    // Effect to initialize Square after payment form is rendered
    useEffect(() => {
        if (showPaymentModal && !isPaymentMethodsLoading) {
            // Wait for the payment form to be rendered
            const timer = setTimeout(() => {
                const cardContainer = document.getElementById('card-container');
                if (cardContainer) {
                    console.log('Card container found, initializing Square payments');
                    initializeSquare();
                } else {
                    console.log('Card container not found yet, waiting...');
                }
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [showPaymentModal, isPaymentMethodsLoading]);

    return (
        <div className="modalContainer">
            <Modal id="confirmReservationModal" centered show={showPaymentModal} onHide={handleCancel} backdrop="static" className="text-center">
                <Modal.Header className="text-center d-block">
                    <Modal.Title>Total Due: ${cartTotal.toFixed(2)}</Modal.Title>
                    <p>
                        {widgetSettings?.paymentBehavior === 'authorize'
                            ? 'This reservation requires a credit card hold. When you show up for your tee time, you can release the hold and pay with a different method.'
                            : 'This reservation requires payment to book.'
                        }
                    </p>
                    <div className="mt-3 text-start">
                        {reservation?.golfers?.map((golfer, index) => {
                            const rateGroup = typeof golfer.rateGroup === 'number' ? golfer.rateGroup : 0;
                            const rate = course?.rates?.[rateGroup];
                            const fee = reservation.numHoles === 18
                                ? Number(rate?.greenFee18) || 0
                                : Number(rate?.greenFee9) || 0;

                            return (
                                <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <span className="fw-bold">{golfer.name}</span>
                                        <small className="text-muted d-block">
                                            {reservation.numHoles} holes - {rate?.name || 'Standard Rate'}
                                        </small>
                                    </div>
                                    <div className="fw-bold">${fee.toFixed(2)}</div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Header>
                {showSuccess && <div className="alert alert-success" role="alert">{successMessage}</div>}
                {(reservation.expiration || reservation.expiresAt) && (
                    <CountDown
                        expiresAt={reservation.expiresAt}
                        expiration={reservation.expiration}
                        resID={reservation.id}
                        deleteGroup={removeReservationTime}
                        disableAutoDelete={completePaymentLoader || showSuccess}
                    />
                )}
                <Modal.Body>
                    {checkoutError && <div className="alert alert-danger" role="alert">{checkoutError}</div>}

                    {isPaymentMethodsLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading payment methods...</span>
                                </div>
                                <p className="text-muted">Loading payment methods...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="App checkoutForm">
                            {/* Payment Methods */}
                            <div className="space-y-4">
                                {/* Card Payment */}
                                <div className="border rounded-lg p-4">
                                    <p className="text-lg font-semibold mb-4">Pay below using your credit card or device payment method.</p>
                                    <div
                                        id="card-container"
                                        className="w-full h-[100px] bg-white"
                                        style={{ minHeight: '100px' }}
                                    />
                                </div>

                                {/* Apple Pay */}
                                {availablePaymentMethods.includes('applePay') && (
                                    <div className="border rounded-lg p-4">
                                        <h3 className="text-lg font-semibold mb-4">Apple Pay</h3>
                                        {!paymentMethods.applePay ? (
                                            <div className="d-flex justify-content-center align-items-center" style={{ height: '40px' }}>
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                id="apple-pay-button"
                                                className="w-full"
                                                style={{
                                                    minHeight: '40px',
                                                    WebkitAppearance: 'none',
                                                    appearance: 'none',
                                                    backgroundColor: '#000',
                                                    backgroundImage: '-webkit-named-image(apple-pay-logo-white)',
                                                    backgroundPosition: '50% 50%',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundSize: '100% 60%',
                                                    borderRadius: '5px',
                                                    padding: '0px',
                                                    margin: '10px 0px',
                                                    width: '100%',
                                                    height: '40px'
                                                }}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Google Pay */}
                                {availablePaymentMethods.includes('googlePay') && (
                                    <div className="border rounded-lg p-4">
                                        <h3 className="text-lg font-semibold mb-4">Google Pay</h3>
                                        {!paymentMethods.googlePay ? (
                                            <div className="d-flex justify-content-center align-items-center" style={{ height: '40px' }}>
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                id="google-pay-button"
                                                className="w-full h-[40px] bg-white"
                                                style={{ minHeight: '40px' }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="row mt-3">
                                <div className="col">
                                    <button
                                        type="button"
                                        id="completePaymentButton"
                                        className="btn btn-success"
                                        onClick={() => handlePayment('card')}
                                        disabled={completePaymentLoader}
                                    >
                                        {completePaymentLoader && <div className="spinner-border text-light" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>}
                                        {widgetSettings?.paymentBehavior === 'authorize' ? 'Authorize Payment Hold' : 'Complete Payment'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="footerButtons">
                    <Button variant="danger" onClick={handleCancel}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default PayNowModal